import {
  Fragment,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Copy,
  Plus,
  Trash2,
} from "lucide-react";
import {
  GENERIC_TABLE_LIMITS,
  type GenericTableCell,
  type GenericTableColumn,
  type GenericTableColumnType,
  type GenericTableRow,
} from "@playbookai/widget-schemas";
import type { PeopleSource } from "@playbookai/types";
import { RowBadgeView, type RowBadge } from "./row-badge.js";
import { Button } from "../../atoms/Button/Button.js";
import { Checkbox } from "../../atoms/Checkbox/Checkbox.js";
import { Input } from "../../atoms/Input/Input.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import { FormField } from "../FormField/FormField.js";
import { Modal } from "../Modal/Modal.js";
import { Select } from "../Select/Select.js";
import { PeopleField } from "../PeopleField/PeopleField.js";
import type { PeopleFieldValue } from "../PeopleField/people-field.types.js";

export interface EditableDataGridUserResult {
  readonly userId: string;
  readonly displayName: string;
}

/** How a change reached `onChange`, for the host's save (assign-person d25). */
export interface EditableDataGridChangeOptions {
  /** A person was picked in a user cell: the save may add them as Viewer. */
  readonly grantViewerIfMissing?: true;
}

export interface EditableDataGridProps {
  readonly columns: GenericTableColumn[];
  readonly rows: GenericTableRow[];
  /**
   * With `options.grantViewerIfMissing` (a user-cell pick) the host may return
   * a promise that settles with that save; a rejection's `message` shows under
   * the cell and the pick stays on screen.
   */
  readonly onChange: (
    next: {
      columns: GenericTableColumn[];
      rows: GenericTableRow[];
    },
    options?: EditableDataGridChangeOptions,
  ) => void | Promise<void>;
  readonly readOnly?: boolean;
  /**
   * The canvas's people, for user cells: the shared person field, with typed
   * names allowed.
   */
  readonly peopleSource?: PeopleSource | null | undefined;
  readonly resolveUser?: (
    displayName: string,
  ) => Promise<EditableDataGridUserResult | null>;
  readonly columnPolicy?:
    | ((column: GenericTableColumn) => {
        readonly canDelete: boolean;
        readonly canChangeType: boolean;
      })
    | undefined;
  readonly rowDeletePolicy?:
    | ((row: GenericTableRow) => {
        readonly canDelete: boolean;
        readonly reason?: string;
      })
    | undefined;
  /** Return true for local deletion; false when the host owns the command. */
  readonly onDeleteRow?: ((row: GenericTableRow) => boolean) | undefined;
  /** Rows with the same group id must be contiguous in the supplied order. */
  readonly rowGroup?:
    | ((row: GenericTableRow) => {
        readonly id: string;
        readonly label: string;
        readonly defaultCollapsed?: boolean;
      })
    | undefined;
  /**
   * Facts stated beside a row's title. A LIST, because a row can carry more
   * than one independent fact and folding them into a single badge deletes one.
   */
  readonly rowBadge?:
    | ((row: GenericTableRow) => readonly RowBadge[])
    | undefined;
  /**
   * PB-4923 AC5 — the editing lifecycle for ONE cell, supplied by the host.
   *
   * A grid is the surface where "somebody is typing here" matters most: two
   * people fill in one table at once, and a cell is the smallest thing either
   * of them is holding. The handlers arrive already bound to a target because
   * this component stays data-blind — the host owns the addressing grammar and
   * the transport, and the grid only knows where to hang the two callbacks.
   * Absent, every cell behaves exactly as it did before.
   */
  readonly cellSession?:
    | ((
        rowId: string,
        columnId: string,
      ) => {
        readonly onFocus: () => void;
        readonly onBlur: () => void;
      })
    | undefined;
}

type PendingConfirmation =
  | { kind: "delete-column"; columnId: string }
  | { kind: "change-type"; columnId: string; nextType: GenericTableColumnType };

interface GridValidationError {
  readonly message: string;
  readonly row: number;
  readonly column: number;
}

const COLUMN_TYPES: Array<{ value: GenericTableColumnType; label: string }> = [
  { value: "text", label: "Text" },
  { value: "longText", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "singleSelect", label: "Single select" },
  { value: "multiSelect", label: "Multi select" },
  { value: "date", label: "Date" },
  { value: "user", label: "User" },
  { value: "boolean", label: "Boolean" },
];

export function EditableDataGrid({
  columns,
  rows,
  onChange,
  readOnly,
  resolveUser,
  peopleSource,
  columnPolicy,
  rowDeletePolicy,
  onDeleteRow,
  rowGroup,
  rowBadge,
  cellSession,
}: EditableDataGridProps): ReactElement {
  const disabled = readOnly === true;
  const rootRef = useRef<HTMLDivElement>(null);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(
    null,
  );
  const [error, setError] = useState<GridValidationError | null>(null);
  const [activeCell, setActiveCell] = useState("0:0");
  const [groupOverrides, setGroupOverrides] = useState<
    ReadonlyMap<string, boolean>
  >(new Map());

  const commitStructure = (next: {
    columns: GenericTableColumn[];
    rows: GenericTableRow[];
  }): void => {
    setError(null);
    setActiveCell((current) =>
      clampActiveCell(current, next.rows.length, next.columns.length),
    );
    onChange(next);
  };

  const updateColumn = (
    columnId: string,
    patch: Partial<GenericTableColumn>,
  ): void => {
    if (disabled) return;
    setError(null);
    onChange({
      columns: columns.map((column) =>
        column.id === columnId ? { ...column, ...patch } : column,
      ),
      rows,
    });
  };

  const addColumn = (): void => {
    if (disabled || columns.length >= GENERIC_TABLE_LIMITS.columns) return;
    commitStructure({
      columns: [
        ...columns,
        {
          id: nextId("column"),
          label: uniqueColumnLabel(columns),
          type: "text",
          origin: "user",
          width: "medium",
        },
      ],
      rows,
    });
  };

  const addRow = (): void => {
    if (disabled || rows.length >= GENERIC_TABLE_LIMITS.rows) return;
    commitStructure({
      columns,
      rows: [...rows, { id: nextId("row"), origin: "user", cells: {} }],
    });
  };

  const moveColumn = (index: number, offset: -1 | 1): void => {
    const target = index + offset;
    if (disabled || target < 0 || target >= columns.length) return;
    commitStructure({ columns: move(columns, index, target), rows });
  };

  const duplicateColumn = (index: number): void => {
    if (disabled || columns.length >= GENERIC_TABLE_LIMITS.columns) return;
    const source = columns[index];
    if (!source) return;
    const copyId = nextId("column");
    const nextColumns = [...columns];
    nextColumns.splice(index + 1, 0, {
      ...source,
      id: copyId,
      label: uniqueColumnLabel(columns, `${source.label} copy`),
      origin: "user",
    });
    const nextRows = rows.map((row) => {
      const cells = { ...row.cells };
      const sourceCell = row.cells[source.id];
      if (sourceCell) cells[copyId] = sourceCell;
      return { ...row, cells };
    });
    commitStructure({ columns: nextColumns, rows: nextRows });
  };

  const requestDeleteColumn = (columnId: string): void => {
    if (disabled) return;
    const column = columns.find((candidate) => candidate.id === columnId);
    if (column && columnPolicy && !columnPolicy(column).canDelete) return;
    if (populatedCellCount(rows, columnId) > 0) {
      setConfirmation({ kind: "delete-column", columnId });
      return;
    }
    deleteColumn(columnId);
  };

  const deleteColumn = (columnId: string): void => {
    commitStructure({
      columns: columns.filter((column) => column.id !== columnId),
      rows: rows.map((row) => {
        const cells = { ...row.cells };
        delete cells[columnId];
        return { ...row, cells };
      }),
    });
  };

  const requestTypeChange = (
    column: GenericTableColumn,
    nextType: GenericTableColumnType,
  ): void => {
    if (
      disabled ||
      column.type === nextType ||
      (columnPolicy && !columnPolicy(column).canChangeType)
    )
      return;
    const populated = populatedCellCount(rows, column.id) > 0;
    const lossless =
      (column.type === "text" && nextType === "longText") ||
      (column.type === "longText" && nextType === "text");
    if (populated && !lossless) {
      setConfirmation({ kind: "change-type", columnId: column.id, nextType });
      return;
    }
    changeColumnType(column.id, nextType, lossless);
  };

  const changeColumnType = (
    columnId: string,
    nextType: GenericTableColumnType,
    preserveText = false,
  ): void => {
    commitStructure({
      columns: columns.map((column) => {
        if (column.id !== columnId) return column;
        const { options: currentOptions, ...withoutOptions } = column;
        return nextType === "singleSelect" || nextType === "multiSelect"
          ? {
              ...withoutOptions,
              type: nextType,
              options: currentOptions ?? [],
            }
          : { ...withoutOptions, type: nextType };
      }),
      rows: rows.map((row) => {
        const previous = row.cells[columnId];
        const cells = { ...row.cells };
        if (
          preserveText &&
          (previous?.type === "text" || previous?.type === "longText")
        ) {
          cells[columnId] = {
            type: nextType,
            value: previous.value,
          } as GenericTableCell;
        } else {
          delete cells[columnId];
        }
        return { ...row, cells };
      }),
    });
  };

  const updateCell = (
    rowId: string,
    columnId: string,
    cell: GenericTableCell,
    appendRow = false,
    options?: EditableDataGridChangeOptions,
  ): void | Promise<void> => {
    if (disabled) return;
    setError(null);
    const nextRows = rows.map((row) =>
      row.id === rowId
        ? { ...row, cells: { ...row.cells, [columnId]: cell } }
        : row,
    );
    if (appendRow && nextRows.length < GENERIC_TABLE_LIMITS.rows) {
      nextRows.push({ id: nextId("row"), origin: "user", cells: {} });
    }
    const next = { columns, rows: nextRows };
    return options ? onChange(next, options) : onChange(next);
  };

  const updateColumnOptions = (
    column: GenericTableColumn,
    rawLabels: string,
  ): void => {
    const requestedLabels = [
      ...new Set(
        rawLabels
          .split(",")
          .map((label) => label.trim())
          .filter(Boolean),
      ),
    ].slice(0, GENERIC_TABLE_LIMITS.options);
    const existing = column.options ?? [];
    const usedIds = new Set<string>();
    for (const row of rows) {
      const cell = row.cells[column.id];
      if (cell?.type === "singleSelect") usedIds.add(cell.optionId);
      if (cell?.type === "multiSelect") {
        for (const optionId of cell.optionIds) usedIds.add(optionId);
      }
    }
    const requestedOptions = requestedLabels.map((label) => {
      const match = existing.find((option) => option.label === label);
      return (
        match ?? {
          id: nextId("option"),
          label: label.slice(0, GENERIC_TABLE_LIMITS.labelCharacters),
        }
      );
    });
    const requestedIds = new Set(requestedOptions.map((option) => option.id));
    const omittedUsedOptions = existing.filter(
      (option) => usedIds.has(option.id) && !requestedIds.has(option.id),
    );
    const optionalSlots = Math.max(
      0,
      GENERIC_TABLE_LIMITS.options -
        requestedOptions.filter((option) => usedIds.has(option.id)).length -
        omittedUsedOptions.length,
    );
    let optionalCount = 0;
    const nextOptions = requestedOptions.filter((option) => {
      if (usedIds.has(option.id)) return true;
      if (optionalCount >= optionalSlots) return false;
      optionalCount += 1;
      return true;
    });
    for (const option of omittedUsedOptions) {
      nextOptions.push(option);
    }
    updateColumn(column.id, {
      options: nextOptions,
    });
  };

  const moveRow = (index: number, offset: -1 | 1): void => {
    const target = index + offset;
    if (disabled || target < 0 || target >= rows.length) return;
    commitStructure({ columns, rows: move(rows, index, target) });
  };

  const duplicateRow = (index: number): void => {
    if (disabled || rows.length >= GENERIC_TABLE_LIMITS.rows) return;
    const source = rows[index];
    if (!source) return;
    const nextRows = [...rows];
    nextRows.splice(index + 1, 0, {
      ...source,
      id: nextId("row"),
      origin: "user",
      cells: { ...source.cells },
    });
    commitStructure({ columns, rows: nextRows });
  };

  const handleGridKey = (
    event: KeyboardEvent<HTMLTableCellElement>,
    rowIndex: number,
    columnIndex: number,
  ): void => {
    if (event.key === "Tab") {
      if (event.target !== event.currentTarget) return;
      const focusableControls = event.currentTarget.querySelectorAll(
        "input:not(:disabled),textarea:not(:disabled),button:not(:disabled)",
      );
      if (focusableControls.length > 1) return;
      const current = rowIndex * columns.length + columnIndex;
      const next = current + (event.shiftKey ? -1 : 1);
      if (next < 0 || next >= rows.length * columns.length) return;
      event.preventDefault();
      const nextRow = Math.floor(next / columns.length);
      const nextColumn = next % columns.length;
      setActiveCell(`${nextRow}:${nextColumn}`);
      const target = rootRef.current?.querySelector<HTMLElement>(
        `[data-grid-cell="${nextRow}:${nextColumn}"]`,
      );
      const editor = target?.querySelector<HTMLElement>(
        "input:not(:disabled),textarea:not(:disabled),button:not(:disabled)",
      );
      (editor ?? target)?.focus();
      return;
    }
    if (event.target !== event.currentTarget) return;
    if (
      !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
      if (event.key === "Enter" || event.key === "F2") {
        event.currentTarget
          .querySelector<HTMLElement>(
            "input:not(:disabled),textarea:not(:disabled),button:not(:disabled)",
          )
          ?.focus();
      }
      return;
    }
    event.preventDefault();
    const nextRow =
      rowIndex +
      (event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0);
    const nextColumn =
      columnIndex +
      (event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0);
    const target = rootRef.current?.querySelector<HTMLElement>(
      `[data-grid-cell="${nextRow}:${nextColumn}"]`,
    );
    if (target) {
      setActiveCell(`${nextRow}:${nextColumn}`);
      target.focus();
    }
  };

  const handlePaste = (
    event: ClipboardEvent<HTMLTableCellElement>,
    startRow: number,
    startColumn: number,
  ): void => {
    if (disabled) return;
    if (event.target !== event.currentTarget) return;
    const matrix = event.clipboardData
      .getData("text/plain")
      .replace(/\r/g, "")
      .split("\n")
      .filter((line, index, lines) => line || index < lines.length - 1)
      .map((line) => line.split("\t"));
    if (!matrix.length) return;
    event.preventDefault();
    const nextRows = rows.map((row) => ({ ...row, cells: { ...row.cells } }));
    while (
      nextRows.length < startRow + matrix.length &&
      nextRows.length < GENERIC_TABLE_LIMITS.rows
    ) {
      nextRows.push({ id: nextId("row"), origin: "user", cells: {} });
    }
    const truncated = startRow + matrix.length > nextRows.length;
    let firstInvalid: { row: number; column: number } | null = null;
    for (const [rowOffset, values] of matrix.entries()) {
      const row = nextRows[startRow + rowOffset];
      if (!row) continue;
      for (const [columnOffset, raw] of values.entries()) {
        const column = columns[startColumn + columnOffset];
        if (!column) continue;
        const parsed = cellFromText(column, raw);
        if (parsed) row.cells[column.id] = parsed;
        else if (firstInvalid === null) {
          firstInvalid = {
            row: startRow + rowOffset,
            column: startColumn + columnOffset,
          };
        }
      }
    }
    setError(
      firstInvalid
        ? {
            message: "Some pasted values did not match their column type.",
            row: firstInvalid.row,
            column: firstInvalid.column,
          }
        : truncated
          ? {
              message:
                "Some pasted rows were dropped because the table row limit was reached.",
              row: Math.max(nextRows.length - 1, 0),
              column: startColumn,
            }
          : null,
    );
    onChange({ columns, rows: nextRows });
  };

  const confirmPending = (): void => {
    if (!confirmation) return;
    if (confirmation.kind === "delete-column") {
      deleteColumn(confirmation.columnId);
    } else {
      changeColumnType(confirmation.columnId, confirmation.nextType);
    }
    setConfirmation(null);
  };

  return (
    <div ref={rootRef} className="flex min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled || columns.length >= GENERIC_TABLE_LIMITS.columns}
          onClick={addColumn}
        >
          <Plus className="size-3.5" aria-hidden="true" /> Add column
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled || rows.length >= GENERIC_TABLE_LIMITS.rows}
          onClick={addRow}
        >
          <Plus className="size-3.5" aria-hidden="true" /> Add row
        </Button>
        <span className="text-xs text-pb-text-muted">
          {rows.length} rows · {columns.length} columns
        </span>
      </div>
      {error ? (
        <div
          role="alert"
          className="rounded-md border border-pb-red/40 bg-pb-red/8 p-2 text-sm text-pb-red-text"
        >
          {error.message}{" "}
          <button
            type="button"
            className="font-semibold underline underline-offset-2 active:opacity-70"
            onClick={() => {
              setActiveCell(`${error.row}:${error.column}`);
              rootRef.current
                ?.querySelector<HTMLElement>(
                  `[data-grid-cell="${error.row}:${error.column}"]`,
                )
                ?.focus();
            }}
          >
            Go to first invalid cell
          </button>
        </div>
      ) : null}
      <div className="max-h-[58vh] overflow-auto rounded-lg border border-pb-border">
        <table
          role="grid"
          aria-label="Editable structured table"
          className="min-w-full border-separate border-spacing-0 text-sm"
        >
          <thead className="sticky top-0 z-10 bg-pb-card-elevated">
            <tr>
              <th className="sticky start-0 z-20 w-24 min-w-24 border-b border-e border-pb-border bg-pb-card-elevated p-2 text-start text-xs text-pb-text-muted">
                Rows
              </th>
              {columns.map((column, columnIndex) => (
                <th
                  key={column.id}
                  scope="col"
                  className="min-w-52 border-b border-e border-pb-border p-2 align-top font-normal"
                >
                  <div className="grid gap-2">
                    <DraftField
                      ariaLabel={`Rename ${column.label} column`}
                      value={column.label}
                      readOnly={disabled}
                      onCommit={(label) =>
                        updateColumn(column.id, {
                          label:
                            label
                              .trim()
                              .slice(0, GENERIC_TABLE_LIMITS.labelCharacters) ||
                            column.label,
                        })
                      }
                    />
                    <Select
                      label={`Type for ${column.label}`}
                      value={column.type}
                      disabled={
                        disabled ||
                        (columnPolicy !== undefined &&
                          !columnPolicy(column).canChangeType)
                      }
                      options={COLUMN_TYPES}
                      onChange={(type) =>
                        requestTypeChange(
                          column,
                          type as GenericTableColumnType,
                        )
                      }
                    />
                    {column.type === "singleSelect" ||
                    column.type === "multiSelect" ? (
                      <div className="grid gap-1">
                        <DraftField
                          ariaLabel={`Options for ${column.label}`}
                          value={(column.options ?? [])
                            .map((option) => option.label)
                            .join(", ")}
                          readOnly={disabled}
                          onCommit={(labels) =>
                            updateColumnOptions(column, labels)
                          }
                        />
                        <span className="text-start text-xs text-pb-text-muted">
                          Separate choices with commas. Choices in use remain
                          until their cells are changed.
                        </span>
                      </div>
                    ) : null}
                    <div className="flex gap-1">
                      <Button
                        iconOnly
                        aria-label={`Move ${column.label} left`}
                        size="sm"
                        variant="ghost"
                        disabled={disabled || columnIndex === 0}
                        onClick={() => moveColumn(columnIndex, -1)}
                      >
                        <ArrowLeft className="size-3.5" />
                      </Button>
                      <Button
                        iconOnly
                        aria-label={`Move ${column.label} right`}
                        size="sm"
                        variant="ghost"
                        disabled={
                          disabled || columnIndex === columns.length - 1
                        }
                        onClick={() => moveColumn(columnIndex, 1)}
                      >
                        <ArrowRight className="size-3.5" />
                      </Button>
                      <Button
                        iconOnly
                        aria-label={`Duplicate ${column.label}`}
                        size="sm"
                        variant="ghost"
                        disabled={disabled}
                        onClick={() => duplicateColumn(columnIndex)}
                      >
                        <Copy className="size-3.5" />
                      </Button>
                      <Button
                        iconOnly
                        aria-label={`Delete ${column.label}`}
                        size="sm"
                        variant="ghost"
                        disabled={
                          disabled ||
                          (columnPolicy !== undefined &&
                            !columnPolicy(column).canDelete)
                        }
                        onClick={() => requestDeleteColumn(column.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                </th>
              ))}
              <th className="w-32 border-b border-pb-border p-2 align-bottom">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  onClick={addColumn}
                >
                  <Plus className="size-3.5" /> Column
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const deletePolicy = rowDeletePolicy?.(row);
              const deleteReasonId = `delete-row-reason-${row.id}`;
              const badges = rowBadge?.(row) ?? [];
              const group = rowGroup?.(row);
              const previousGroup =
                rowIndex > 0 ? rowGroup?.(rows[rowIndex - 1]!) : undefined;
              const startsGroup =
                group !== undefined && group.id !== previousGroup?.id;
              const collapsed = group
                ? (groupOverrides.get(group.id) ??
                  group.defaultCollapsed === true)
                : false;
              return (
                <Fragment key={row.id}>
                  {startsGroup ? (
                    <tr>
                      <th
                        colSpan={columns.length + 2}
                        className="sticky start-0 border-b border-pb-border bg-pb-card-elevated px-3 py-2 text-start text-xs font-semibold text-pb-text"
                      >
                        <button
                          type="button"
                          aria-expanded={!collapsed}
                          onClick={() =>
                            setGroupOverrides((current) => {
                              const next = new Map(current);
                              next.set(group.id, !collapsed);
                              return next;
                            })
                          }
                          className="w-full text-start"
                        >
                          {collapsed ? "▸" : "▾"} {group.label}
                        </button>
                      </th>
                    </tr>
                  ) : null}
                  {!collapsed ? (
                    <tr>
                      <th
                        scope="row"
                        className="sticky start-0 z-raised border-b border-e border-pb-border bg-pb-card p-1"
                      >
                        <div className="flex justify-center gap-0.5">
                          <Button
                            iconOnly
                            aria-label={`Move row ${rowIndex + 1} up`}
                            size="sm"
                            variant="ghost"
                            disabled={disabled || rowIndex === 0}
                            onClick={() => moveRow(rowIndex, -1)}
                          >
                            <ArrowUp className="size-3.5" />
                          </Button>
                          <Button
                            iconOnly
                            aria-label={`Move row ${rowIndex + 1} down`}
                            size="sm"
                            variant="ghost"
                            disabled={disabled || rowIndex === rows.length - 1}
                            onClick={() => moveRow(rowIndex, 1)}
                          >
                            <ArrowDown className="size-3.5" />
                          </Button>
                          <Button
                            iconOnly
                            aria-label={`Duplicate row ${rowIndex + 1}`}
                            size="sm"
                            variant="ghost"
                            disabled={disabled}
                            onClick={() => duplicateRow(rowIndex)}
                          >
                            <Copy className="size-3.5" />
                          </Button>
                          <Button
                            iconOnly
                            aria-label={`Delete row ${rowIndex + 1}`}
                            size="sm"
                            variant="ghost"
                            disabled={
                              disabled || deletePolicy?.canDelete === false
                            }
                            {...(deletePolicy?.reason
                              ? { "aria-describedby": deleteReasonId }
                              : {})}
                            onClick={() => {
                              if (onDeleteRow && !onDeleteRow(row)) return;
                              commitStructure({
                                columns,
                                rows: rows.filter((item) => item.id !== row.id),
                              });
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                        {deletePolicy?.reason ? (
                          <span
                            id={deleteReasonId}
                            className="mt-1 block text-center text-dense-2 text-pb-text-muted"
                          >
                            {deletePolicy.reason}
                          </span>
                        ) : null}
                        {badges.length === 0 ? null : (
                          <span
                            data-testid="row-badges"
                            className="mt-1 flex flex-wrap justify-center gap-1"
                          >
                            {badges.map((badge, badgeIndex) => (
                              <RowBadgeView
                                key={`${badge.label}-${badgeIndex}`}
                                badge={badge}
                              />
                            ))}
                          </span>
                        )}
                      </th>
                      {columns.map((column, columnIndex) => (
                        <td
                          key={column.id}
                          role="gridcell"
                          tabIndex={
                            activeCell === `${rowIndex}:${columnIndex}` ? 0 : -1
                          }
                          data-grid-cell={`${rowIndex}:${columnIndex}`}
                          className="border-b border-e border-pb-border p-1 align-top focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal/50"
                          onFocus={() =>
                            setActiveCell(`${rowIndex}:${columnIndex}`)
                          }
                          onKeyDown={(event) =>
                            handleGridKey(event, rowIndex, columnIndex)
                          }
                          onPaste={(event) =>
                            handlePaste(event, rowIndex, columnIndex)
                          }
                        >
                          <div className="min-h-9 rounded">
                            <EditableCell
                              column={column}
                              cell={row.cells[column.id]}
                              readOnly={disabled}
                              onChange={(cell, options) =>
                                updateCell(
                                  row.id,
                                  column.id,
                                  cell,
                                  false,
                                  options,
                                )
                              }
                              onValidationError={() =>
                                setError({
                                  message: `${column.label} has an invalid value.`,
                                  row: rowIndex,
                                  column: columnIndex,
                                })
                              }
                              {...(resolveUser ? { resolveUser } : {})}
                              {...(peopleSource ? { peopleSource } : {})}
                              {...(cellSession
                                ? { session: cellSession(row.id, column.id) }
                                : {})}
                              {...(rowIndex === rows.length - 1 &&
                              columnIndex === columns.length - 1
                                ? {
                                    onCommitLast: (cell: GenericTableCell) =>
                                      updateCell(row.id, column.id, cell, true),
                                  }
                                : {})}
                            />
                          </div>
                        </td>
                      ))}
                      <td className="border-b border-pb-border" />
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
            <tr>
              <td colSpan={columns.length + 2} className="p-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  onClick={addRow}
                >
                  <Plus className="size-3.5" /> Add row
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <Modal
        open={confirmation !== null}
        onClose={() => setConfirmation(null)}
        title={
          confirmation?.kind === "delete-column"
            ? "Delete populated column?"
            : "Change column type?"
        }
        description={confirmationDescription(confirmation, columns, rows)}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmation(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmPending}>
              Continue
            </Button>
          </>
        }
      >
        <p className="text-sm text-pb-text-muted">
          {confirmationPreview(confirmation, columns, rows)}
        </p>
      </Modal>
    </div>
  );
}

/** The two focus callbacks a cell hangs its host's edit lease on. */
type CellSession = {
  readonly onFocus: () => void;
  readonly onBlur: () => void;
};

/**
 * A user cell as the shared person field (assign-person d7): a picked member,
 * or a typed name with no link. A pick asks the host to add the person as
 * Viewer when they have no access; a refusal shows under the cell and the pick
 * stays on screen until the next choice.
 */
function UserCellPeopleField({
  column,
  user,
  source,
  readOnly,
  onChange,
  session,
}: {
  readonly column: GenericTableColumn;
  readonly user: Extract<GenericTableCell, { type: "user" }> | null;
  readonly source: PeopleSource;
  readonly readOnly: boolean;
  readonly onChange: (
    next: GenericTableCell,
    options?: EditableDataGridChangeOptions,
  ) => void | Promise<void>;
  readonly session?: CellSession;
}): ReactElement {
  const [failed, setFailed] = useState<{
    readonly value: PeopleFieldValue;
    readonly message: string;
  } | null>(null);
  const stored: PeopleFieldValue = user
    ? user.userId
      ? { kind: "person", id: user.userId, name: user.displayName }
      : { kind: "typed", name: user.displayName }
    : null;
  // Only the newest pick may report a failure: a slow older pick that fails
  // after a newer one saved must not put its stale person back in the cell.
  const latestPick = useRef(0);
  const change = (next: PeopleFieldValue): void => {
    setFailed(null);
    const pick = ++latestPick.current;
    const saved = onChange(
      next === null
        ? { type: "empty" }
        : {
            type: "user",
            userId: next.kind === "person" ? next.id : null,
            displayName: next.name.slice(
              0,
              GENERIC_TABLE_LIMITS.userDisplayNameCharacters,
            ),
          },
      next?.kind === "person" ? { grantViewerIfMissing: true } : undefined,
    );
    if (!(saved instanceof Promise)) return;
    saved.catch((reason: unknown) => {
      if (pick !== latestPick.current) return;
      setFailed({
        value: next,
        message:
          reason instanceof Error && reason.message
            ? reason.message
            : "Edit not saved",
      });
    });
  };
  return (
    <div className="p-1">
      <FormField {...(failed ? { error: failed.message } : {})}>
        <PeopleField
          value={failed ? failed.value : stored}
          onChange={change}
          source={source}
          label={`${column.label} person`}
          allowTyped
          size="sm"
          readOnly={readOnly}
          {...(session ?? {})}
        />
      </FormField>
    </div>
  );
}

function EditableCell({
  column,
  cell,
  readOnly,
  onChange,
  resolveUser,
  peopleSource,
  onCommitLast,
  onValidationError,
  session,
}: {
  readonly column: GenericTableColumn;
  readonly cell: GenericTableCell | undefined;
  readonly readOnly: boolean;
  readonly onChange: (
    next: GenericTableCell,
    options?: EditableDataGridChangeOptions,
  ) => void | Promise<void>;
  readonly resolveUser?: EditableDataGridProps["resolveUser"];
  readonly peopleSource?: PeopleSource;
  readonly onCommitLast?: (next: GenericTableCell) => void;
  readonly onValidationError?: () => void;
  readonly session?: CellSession;
}): ReactElement {
  if (column.type === "boolean") {
    return (
      <div className="flex min-h-9 items-center px-2">
        <Checkbox
          aria-label={`${column.label} value`}
          checked={cell?.type === "boolean" ? cell.value : false}
          disabled={readOnly}
          {...(session ?? {})}
          onChange={(value) => onChange({ type: "boolean", value })}
        />
      </div>
    );
  }
  if (column.type === "singleSelect") {
    return (
      <Select
        label={`${column.label} value`}
        value={cell?.type === "singleSelect" ? cell.optionId : ""}
        disabled={readOnly}
        placeholder="Select…"
        {...(session ?? {})}
        options={(column.options ?? []).map((option) => ({
          value: option.id,
          label: option.label,
        }))}
        onChange={(optionId) => onChange({ type: "singleSelect", optionId })}
      />
    );
  }
  if (column.type === "multiSelect") {
    const selected = cell?.type === "multiSelect" ? cell.optionIds : [];
    return (
      <div className="grid gap-1 p-1">
        {(column.options ?? []).map((option) => (
          <Checkbox
            key={option.id}
            label={option.label}
            checked={selected.includes(option.id)}
            disabled={readOnly}
            {...(session ?? {})}
            onChange={(checked) =>
              onChange({
                type: "multiSelect",
                optionIds: checked
                  ? [...selected, option.id]
                  : selected.filter((id) => id !== option.id),
              })
            }
          />
        ))}
      </div>
    );
  }
  if (column.type === "user") {
    const user = cell?.type === "user" ? cell : null;
    if (peopleSource) {
      return (
        <UserCellPeopleField
          column={column}
          user={user}
          source={peopleSource}
          readOnly={readOnly}
          onChange={onChange}
          {...(session ? { session } : {})}
        />
      );
    }
    return (
      <div className="grid gap-1">
        <DraftField
          value={user?.displayName ?? ""}
          readOnly={readOnly}
          ariaLabel={`${column.label} person`}
          {...(session ? { session } : {})}
          onCommit={async (displayName) => {
            const resolved = resolveUser
              ? await resolveUser(displayName)
              : null;
            onChange({
              type: "user",
              userId: resolved?.userId ?? null,
              displayName: resolved?.displayName ?? displayName,
            });
          }}
        />
        {user && user.userId === null ? (
          <span className="px-2 text-xs text-pb-amber-text">
            Unresolved person
          </span>
        ) : null}
      </div>
    );
  }
  const current =
    cell?.type === column.type && "value" in cell ? String(cell.value) : "";
  return (
    <DraftField
      value={current}
      readOnly={readOnly}
      multiline={column.type === "longText"}
      inputType={column.type === "date" ? "date" : "text"}
      ariaLabel={`${column.label} value`}
      {...(session ? { session } : {})}
      onCommit={(raw) => {
        const parsed = cellFromText(column, raw);
        if (parsed) {
          if (onCommitLast) onCommitLast(parsed);
          else onChange(parsed);
          return true;
        }
        onValidationError?.();
        return false;
      }}
    />
  );
}

function DraftField({
  value,
  readOnly,
  multiline,
  inputType,
  ariaLabel,
  onCommit,
  session,
}: {
  readonly value: string;
  readonly readOnly: boolean;
  readonly multiline?: boolean;
  readonly inputType?: "text" | "date";
  readonly ariaLabel: string;
  readonly onCommit: (
    value: string,
  ) => boolean | void | Promise<boolean | void>;
  readonly session?: CellSession;
}): ReactElement {
  const [draftState, setDraftState] = useState({ source: value, draft: value });
  const draft = draftState.source === value ? draftState.draft : value;
  const setDraft = (next: string): void =>
    setDraftState({ source: value, draft: next });
  const skipBlurCommit = useRef(false);
  const commit = (): void => {
    if (draft === value) return;
    void Promise.resolve(onCommit(draft));
  };
  const common = {
    "aria-label": ariaLabel,
    value: draft,
    disabled: readOnly,
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => setDraft(event.target.value),
    onFocus: () => session?.onFocus(),
    onBlur: () => {
      // Released on EVERY blur, including the Enter path that skips the commit:
      // the claim tracks who is in the cell, not whether the value moved.
      session?.onBlur();
      if (skipBlurCommit.current) {
        skipBlurCommit.current = false;
        return;
      }
      commit();
    },
    onKeyDown: (
      event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setDraft(value);
      }
      if (event.key === "Enter" && !multiline) {
        skipBlurCommit.current = true;
        commit();
        event.currentTarget.blur();
      }
    },
  };
  return multiline ? (
    <Textarea {...common} className="min-h-20" />
  ) : (
    <Input {...common} type={inputType ?? "text"} />
  );
}

function confirmationDescription(
  confirmation: PendingConfirmation | null,
  columns: GenericTableColumn[],
  rows: GenericTableRow[],
): string {
  if (!confirmation) return "";
  const column = columns.find((item) => item.id === confirmation.columnId);
  const count = populatedCellCount(rows, confirmation.columnId);
  return confirmation.kind === "delete-column"
    ? `Delete ${column?.label ?? "this column"} and ${count} populated cell${count === 1 ? "" : "s"}?`
    : `Convert ${column?.label ?? "this column"} from ${column?.type ?? "its current type"} to ${confirmation.nextType}?`;
}

function confirmationPreview(
  confirmation: PendingConfirmation | null,
  columns: GenericTableColumn[],
  rows: GenericTableRow[],
): string {
  if (!confirmation) return "";
  const count = populatedCellCount(rows, confirmation.columnId);
  return confirmation.kind === "change-type"
    ? `${count} populated cell${count === 1 ? "" : "s"} cannot be converted losslessly and will be cleared. Other columns and rows are preserved.`
    : `This removes ${count} populated cell${count === 1 ? "" : "s"} in the selected column. Other columns and rows are preserved.`;
}

function cellFromText(
  column: GenericTableColumn,
  raw: string,
): GenericTableCell | null {
  if (raw === "") return { type: "empty" };
  if (column.type === "text")
    return {
      type: "text",
      value: raw.slice(0, GENERIC_TABLE_LIMITS.textCellCharacters),
    };
  if (column.type === "longText")
    return {
      type: "longText",
      value: raw.slice(0, GENERIC_TABLE_LIMITS.textCellCharacters),
    };
  if (column.type === "number") {
    const decimal = raw.trim();
    if (!/^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/.test(decimal)) {
      return null;
    }
    const value = Number(decimal);
    return Number.isFinite(value) ? { type: "number", value } : null;
  }
  if (column.type === "date") {
    return /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? { type: "date", value: raw }
      : null;
  }
  if (column.type === "boolean") {
    if (/^(true|yes|1)$/i.test(raw)) return { type: "boolean", value: true };
    if (/^(false|no|0)$/i.test(raw)) return { type: "boolean", value: false };
    return null;
  }
  if (column.type === "user") {
    return {
      type: "user",
      userId: null,
      displayName: raw.slice(0, GENERIC_TABLE_LIMITS.userDisplayNameCharacters),
    };
  }
  if (column.type === "singleSelect") {
    const option = (column.options ?? []).find(
      (candidate) => candidate.id === raw || candidate.label === raw,
    );
    return option ? { type: "singleSelect", optionId: option.id } : null;
  }
  const requested = raw
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const options = requested.map((item) =>
    (column.options ?? []).find(
      (candidate) => candidate.id === item || candidate.label === item,
    ),
  );
  return options.every(Boolean)
    ? { type: "multiSelect", optionIds: options.map((option) => option!.id) }
    : null;
}

function populatedCellCount(
  rows: readonly GenericTableRow[],
  columnId: string,
): number {
  return rows.filter((row) => {
    const cell = row.cells[columnId];
    return cell !== undefined && cell.type !== "empty";
  }).length;
}

function clampActiveCell(
  activeCell: string,
  rowCount: number,
  columnCount: number,
): string {
  const [rowText, columnText] = activeCell.split(":");
  const row = Number.parseInt(rowText ?? "0", 10);
  const column = Number.parseInt(columnText ?? "0", 10);
  const nextRow = Math.max(
    0,
    Math.min(Number.isFinite(row) ? row : 0, Math.max(rowCount - 1, 0)),
  );
  const nextColumn = Math.max(
    0,
    Math.min(
      Number.isFinite(column) ? column : 0,
      Math.max(columnCount - 1, 0),
    ),
  );
  return `${nextRow}:${nextColumn}`;
}

function move<T>(values: T[], from: number, to: number): T[] {
  const next = [...values];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

let fallbackId = 0;
function nextId(prefix: string): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  fallbackId += 1;
  return `${prefix}-${Date.now().toString(36)}-${fallbackId}`;
}

function uniqueColumnLabel(
  columns: readonly GenericTableColumn[],
  preferredBase?: string,
): string {
  const labels = new Set(columns.map((column) => column.label));
  if (preferredBase) {
    const base = preferredBase
      .trim()
      .slice(0, GENERIC_TABLE_LIMITS.labelCharacters);
    if (!labels.has(base)) return base;
    let copyIndex = 2;
    while (true) {
      const suffix = ` ${copyIndex}`;
      const candidate = `${base.slice(
        0,
        GENERIC_TABLE_LIMITS.labelCharacters - suffix.length,
      )}${suffix}`;
      if (!labels.has(candidate)) return candidate;
      copyIndex += 1;
    }
  }
  let index = columns.length + 1;
  while (labels.has(`Column ${index}`)) index += 1;
  return `Column ${index}`;
}
